import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on, Action } from '@ngrx/store';
import {
  GMServiceVisitByDealer,
  GMServiceVisitByVin,
  GMServiceVisitDtc,
  GMServiceVisitDtcCount,
  GMServiceVisitVehicleData,
} from '@quorum/models/oem-resource';

import * as SvcVehicleDiagnosticsRootActions from './svc-vehicle-diagnostics-root.actions';
import { SvcVehicleDiagnosticsRootEntity } from './svc-vehicle-diagnostics-root.models';

export const SVC_VEHICLE_DIAGNOSTICS_ROOT_FEATURE_KEY = 'svcVehicleDiagnosticsRoot';

export interface State {
  GM: gmVehicleDiagnosticsState;
}

export enum status {
  idle = 'idle',
  loading = 'loading',
  succeeded = 'succeeded',
  failed = 'failed',
}

//#region GM
export interface serviceVisitByVinState extends EntityState<GMServiceVisitByVin> {
  status: status;
  error: { reason: string; error: string; statusCode: number; message: string } | null;
}

export const serviceVisitByVinAdapter: EntityAdapter<GMServiceVisitByVin> = createEntityAdapter<GMServiceVisitByVin>({
  selectId: (gmServiceVisitByVin: GMServiceVisitByVin) => gmServiceVisitByVin.serviceVisitId,
});

export const initialServiceVisitByVinState: serviceVisitByVinState = serviceVisitByVinAdapter.getInitialState({
  status: status.idle,
  error: null,
});

export interface serviceVisitByDealerState extends EntityState<GMServiceVisitByDealer> {
  status: status;
  error: { reason: string; error: string; statusCode: number; message: string } | null;
}

export const serviceVisitByDealerAdapter: EntityAdapter<GMServiceVisitByDealer> =
  createEntityAdapter<GMServiceVisitByDealer>({
    selectId: (gmServiceVisitByDealer: GMServiceVisitByDealer) => gmServiceVisitByDealer.serviceVisitId,
  });

export const initialServiceVisitByDealerState: serviceVisitByDealerState = serviceVisitByDealerAdapter.getInitialState({
  status: status.idle,
  error: null,
});

export interface linkedServiceVisitDtcState extends EntityState<GMServiceVisitDtc> {
  status: status;
  error: { reason: string; error: string; statusCode: number; message: string } | null;
}

export const linkedServiceVisitDtcAdapter: EntityAdapter<GMServiceVisitDtc> = createEntityAdapter<GMServiceVisitDtc>({
  selectId: (gmServiceVisitDtc: GMServiceVisitDtc) => gmServiceVisitDtc.dtcCode,
});

export const initialLinkedServiceVisitDtcState: linkedServiceVisitDtcState =
  linkedServiceVisitDtcAdapter.getInitialState({
    status: status.idle,
    error: null,
  });

export interface linkedServiceVisitMpviState extends EntityState<GMServiceVisitVehicleData> {
  status: status;
  error: { reason: string; error: string; statusCode: number; message: string } | null;
}

export const linkedServiceVisitMpviAdapter: EntityAdapter<GMServiceVisitVehicleData> =
  createEntityAdapter<GMServiceVisitVehicleData>({
    selectId: (gmServiceVisitVehicleData: GMServiceVisitVehicleData) => gmServiceVisitVehicleData.displayName,
  });

export const initialLinkedServiceVisitMpviState: linkedServiceVisitMpviState =
  linkedServiceVisitMpviAdapter.getInitialState({
    status: status.idle,
    error: null,
  });

export interface linkedServiceVisitEcuState extends EntityState<GMServiceVisitDtcCount> {
  status: status;
  error: { reason: string; error: string; statusCode: number; message: string } | null;
}

export const linkedServiceVisitEcuAdapter: EntityAdapter<GMServiceVisitDtcCount> =
  createEntityAdapter<GMServiceVisitDtcCount>({
    selectId: (gmServiceVisitDtcCount: GMServiceVisitDtcCount) => gmServiceVisitDtcCount.ecuId,
  });

export const initialLinkedServiceVisitEcuState: linkedServiceVisitEcuState =
  linkedServiceVisitEcuAdapter.getInitialState({
    status: status.idle,
    error: null,
  });

export interface gmVehicleDiagnosticsState {
  serviceVisitsByVin: serviceVisitByVinState;
  serviceVisitsByDealer: serviceVisitByDealerState;
  linkedServiceVisitId: string | null;
  linkedServiceVisitDtc: linkedServiceVisitDtcState;
  linkedServiceVisitMpvi: linkedServiceVisitMpviState;
  linkedServiceVisitEcu: linkedServiceVisitEcuState;
}

export const initialGMVehicleDiagnosticsState: gmVehicleDiagnosticsState = {
  serviceVisitsByVin: initialServiceVisitByVinState,
  serviceVisitsByDealer: initialServiceVisitByDealerState,
  linkedServiceVisitId: null,
  linkedServiceVisitDtc: initialLinkedServiceVisitDtcState,
  linkedServiceVisitMpvi: initialLinkedServiceVisitMpviState,
  linkedServiceVisitEcu: initialLinkedServiceVisitEcuState,
};

//#endregion
export interface SvcVehicleDiagnosticsRootPartialState {
  readonly [SVC_VEHICLE_DIAGNOSTICS_ROOT_FEATURE_KEY]: State;
}

export const initialState: State = {
  // set initial required properties
  GM: initialGMVehicleDiagnosticsState,
};

const svcVehicleDiagnosticsRootReducer = createReducer(
  initialState,

  on(SvcVehicleDiagnosticsRootActions.getGMServiceVisitByVin, (state) => ({
    ...state,
    GM: {
      ...state.GM,
      serviceVisitsByVin: { ...initialServiceVisitByVinState, status: status.loading, error: null },
    },
  })),

  on(SvcVehicleDiagnosticsRootActions.getGMServiceVisitByVinSuccess, (state, { serviceVisits }) => ({
    ...state,
    GM: {
      ...state.GM,
      serviceVisitsByVin: serviceVisitByVinAdapter.upsertMany(serviceVisits, {
        ...state.GM.serviceVisitsByVin,
        status: status.succeeded,
        error: null,
      }),
    },
  })),

  on(SvcVehicleDiagnosticsRootActions.getGMServiceVisitByVinError, (state, { error }) => ({
    ...state,
    GM: {
      ...state.GM,
      serviceVisitsByVin: serviceVisitByVinAdapter.setAll([], {
        ...state.GM.serviceVisitsByVin,
        status: status.failed,
        error: error,
      }),
    },
  })),

  on(SvcVehicleDiagnosticsRootActions.getGMServiceVisitByDealer, (state) => ({
    ...state,
    GM: {
      ...state.GM,
      serviceVisitsByDealer: { ...state.GM.serviceVisitsByDealer, status: status.loading, error: null },
    },
  })),

  on(SvcVehicleDiagnosticsRootActions.getGMServiceVisitByDealerSuccess, (state, { serviceVisits }) => ({
    ...state,
    GM: {
      ...state.GM,
      serviceVisitsByDealer: serviceVisitByDealerAdapter.setAll(serviceVisits, {
        ...state.GM.serviceVisitsByDealer,
        status: status.succeeded,
        error: null,
      }),
    },
  })),

  on(SvcVehicleDiagnosticsRootActions.getGMServiceVisitByDealerError, (state, { error }) => ({
    ...state,
    GM: {
      ...state.GM,
      serviceVisitsByDealer: serviceVisitByDealerAdapter.setAll([], {
        ...state.GM.serviceVisitsByDealer,
        status: status.failed,
        error: error,
      }),
    },
  })),

  on(SvcVehicleDiagnosticsRootActions.updateLinkedServiceVisitInState, (state, { serviceVisitId }) => ({
    ...state,
    GM: {
      ...state.GM,
      linkedServiceVisitId: serviceVisitId,
    },
  })),

  on(SvcVehicleDiagnosticsRootActions.getLinkedServiceVisitDtc, (state) => ({
    ...state,
    GM: {
      ...state.GM,
      linkedServiceVisitDtc: { ...state.GM.linkedServiceVisitDtc, status: status.loading, error: null },
    },
  })),

  on(SvcVehicleDiagnosticsRootActions.getLinkedServiceVisitDtcSuccess, (state, { linkedServiceVisitDtc }) => ({
    ...state,
    GM: {
      ...state.GM,
      linkedServiceVisitDtc: linkedServiceVisitDtcAdapter.upsertMany(linkedServiceVisitDtc, {
        ...state.GM.linkedServiceVisitDtc,
        status: status.succeeded,
        error: null,
      }),
    },
  })),

  on(SvcVehicleDiagnosticsRootActions.getLinkedServiceVisitDtcError, (state, { error }) => ({
    ...state,
    GM: {
      ...state.GM,
      linkedServiceVisitDtc: linkedServiceVisitDtcAdapter.setAll([], {
        ...state.GM.linkedServiceVisitDtc,
        status: status.failed,
        error: error,
      }),
    },
  })),

  on(SvcVehicleDiagnosticsRootActions.getLinkedServiceVisitMpvi, (state) => ({
    ...state,
    GM: {
      ...state.GM,
      linkedServiceVisitMpvi: { ...state.GM.linkedServiceVisitMpvi, status: status.loading, error: null },
    },
  })),

  on(SvcVehicleDiagnosticsRootActions.getLinkedServiceVisitMpviSuccess, (state, { linkedServiceVisitMpvi }) => ({
    ...state,
    GM: {
      ...state.GM,
      linkedServiceVisitMpvi: linkedServiceVisitMpviAdapter.upsertMany(linkedServiceVisitMpvi, {
        ...state.GM.linkedServiceVisitMpvi,
        status: status.succeeded,
        error: null,
      }),
    },
  })),

  on(SvcVehicleDiagnosticsRootActions.getLinkedServiceVisitMpviError, (state, { error }) => ({
    ...state,
    GM: {
      ...state.GM,
      linkedServiceVisitMpvi: linkedServiceVisitMpviAdapter.setAll([], {
        ...state.GM.linkedServiceVisitMpvi,
        status: status.failed,
        error: error,
      }),
    },
  })),

  on(SvcVehicleDiagnosticsRootActions.getLinkedServiceVisitEcu, (state) => ({
    ...state,
    GM: {
      ...state.GM,
      linkedServiceVisitEcu: { ...state.GM.linkedServiceVisitEcu, status: status.loading, error: null },
    },
  })),

  on(SvcVehicleDiagnosticsRootActions.getLinkedServiceVisitEcuSuccess, (state, { linkedServiceVisitEcu }) => ({
    ...state,
    GM: {
      ...state.GM,
      linkedServiceVisitEcu: linkedServiceVisitEcuAdapter.upsertMany(linkedServiceVisitEcu, {
        ...state.GM.linkedServiceVisitEcu,
        status: status.succeeded,
        error: null,
      }),
    },
  })),

  on(SvcVehicleDiagnosticsRootActions.getLinkedServiceVisitEcuError, (state, { error }) => ({
    ...state,
    GM: {
      ...state.GM,
      linkedServiceVisitEcu: linkedServiceVisitEcuAdapter.setAll([], {
        ...state.GM.linkedServiceVisitEcu,
        status: status.failed,
        error: error,
      }),
    },
  })),

  on(SvcVehicleDiagnosticsRootActions.clearLinkedServiceVisitState, (state) => ({
    GM: {
      ...state.GM,
      linkedServiceVisitId: null,
      linkedServiceVisitDtc: initialLinkedServiceVisitDtcState,
      linkedServiceVisitMpvi: initialLinkedServiceVisitMpviState,
      linkedServiceVisitEcu: initialLinkedServiceVisitEcuState,
    },
  })),

  on(SvcVehicleDiagnosticsRootActions.clearVehicleDiagnosticsState, (state) => ({
    GM: initialGMVehicleDiagnosticsState,
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return svcVehicleDiagnosticsRootReducer(state, action);
}
